import React, { type ReactElement, type ReactNode } from "react";
import { Button, cn } from "@ndd/ui";
import type {
  MessageThreadListItem,
  MessageThreadView,
  MessageView,
} from "../../../../shared/api-types";
import { THEMED_CARD, THEMED_DIVIDE, THEMED_ROW_HOVER } from "../../lib/always-look";
import {
  DELETED_MESSAGE_LABEL,
  attachmentLabel,
  messageTimeLabel,
  olderView,
  type OlderMessages,
  senderLabel,
  type MessageListView,
} from "../../lib/messages-client";
import { patientName } from "../../lib/patients-client";
import { PatientAvatar } from "./patient-list-parts";

// The RD's Messages (NO-925): the list of Clients with a conversation, and one
// Client's threads, read only. White rows on the cream page like Patients and
// Notifications. Every empty state is neutral text. A leaf module (`import
// React`, relative imports, no router, no queries) so `tsx --test` renders it;
// the pages supply the links.

export interface MessageThreadLinkProps {
  patientId: string;
  className: string;
  dataAttributes: { "data-patient-id": string };
  children: ReactNode;
}

export type RenderMessageThreadLink = (props: MessageThreadLinkProps) => ReactElement;

const ROW =
  "flex w-full items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

const NEUTRAL_MESSAGE = "mt-6 text-body text-muted-foreground";

export const EMPTY_MESSAGES_MESSAGE =
  "No messages yet. Conversations with your Clients show up here.";

export const UNAVAILABLE_MESSAGES_MESSAGE =
  "Your messages aren't available yet. Ask an admin to link your Healthie account.";

export const CHOOSE_RD_MESSAGES_MESSAGE =
  "Choose an RD to read their messages. Nothing loads until you select an RD.";

export const NO_THREAD_MESSAGES_MESSAGE = "No messages with this Client yet.";

export const THREAD_NOT_AVAILABLE_MESSAGE =
  "This Client isn't in the messages you can read.";

export const LOAD_OLDER_LABEL = "Load older messages";

export const LOAD_OLDER_FAILED_MESSAGE =
  "Older messages could not be loaded. Try again.";

/**
 * The page title, set in display type under the always theme. An admin who can
 * pick an RD passes the picker, whose title is the heading (as on Appointments).
 */
export function MessagesHeading({ picker }: { picker?: ReactNode }): ReactElement {
  return (
    <>
      {picker ?? (
        <h1 className="font-sans text-3xl font-bold tracking-tight text-foreground brand:text-display brand:leading-[1.15]">
          Messages
        </h1>
      )}
      <p className="mt-2 text-body text-muted-foreground">
        Your conversations with Clients, newest first.
      </p>
    </>
  );
}

function ThreadRowBody({
  thread,
  timezone,
  now,
}: {
  thread: MessageThreadListItem;
  timezone: string;
  now: Date;
}): ReactElement {
  const client = { id: thread.patientId, ...thread };

  return (
    <>
      <PatientAvatar patient={client} size="row" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline gap-3">
          <span className="min-w-0 flex-1 truncate text-body font-semibold text-foreground brand:text-title-sm brand:font-bold">
            {patientName(thread)}
          </span>
          <span className="shrink-0 text-body-sm text-muted-foreground">
            {messageTimeLabel(thread.lastMessageAt, timezone, now)}
          </span>
        </span>
        <span
          className={cn(
            "truncate text-body-sm text-muted-foreground",
            thread.preview === DELETED_MESSAGE_LABEL && "italic"
          )}
        >
          {thread.preview}
        </span>
      </span>
    </>
  );
}

/** The list itself, or the plain message for an empty or unselected one. */
export function MessageList({
  view,
  timezone,
  now,
  renderThreadLink,
}: {
  view: MessageListView;
  timezone: string;
  now: Date;
  renderThreadLink: RenderMessageThreadLink;
}): ReactElement {
  if (view.kind === "no_selection") {
    return <p className={NEUTRAL_MESSAGE}>{CHOOSE_RD_MESSAGES_MESSAGE}</p>;
  }

  if (view.kind === "unavailable") {
    return <p className={NEUTRAL_MESSAGE}>{UNAVAILABLE_MESSAGES_MESSAGE}</p>;
  }

  if (view.kind === "empty") {
    return <p className={NEUTRAL_MESSAGE}>{EMPTY_MESSAGES_MESSAGE}</p>;
  }

  return (
    <ul
      aria-label="Messages"
      className={cn(
        "mt-6 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card",
        THEMED_CARD,
        THEMED_DIVIDE
      )}
    >
      {view.threads.map((thread) => (
        <li key={thread.patientId}>
          {renderThreadLink({
            patientId: thread.patientId,
            className: cn(ROW, THEMED_ROW_HOVER),
            dataAttributes: { "data-patient-id": thread.patientId },
            children: <ThreadRowBody thread={thread} timezone={timezone} now={now} />,
          })}
        </li>
      ))}
    </ul>
  );
}

function MessageItem({
  message,
  timezone,
  now,
}: {
  message: MessageView;
  timezone: string;
  now: Date;
}): ReactElement {
  const fromClient = message.authorKind === "client";

  return (
    <li
      data-message-id={message.id}
      className={cn("flex flex-col gap-1", fromClient ? "items-start" : "items-end")}
    >
      <div className="flex items-baseline gap-2 text-body-sm text-muted-foreground">
        <span className="font-semibold text-foreground">{senderLabel(message)}</span>
        <time dateTime={message.sentAt}>
          {messageTimeLabel(message.sentAt, timezone, now)}
        </time>
        {message.editedAt ? <span>edited</span> : null}
      </div>
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap break-words rounded-xl border border-border px-4 py-2.5 text-body text-foreground",
          fromClient ? "bg-card" : "bg-muted"
        )}
      >
        {message.deleted ? (
          <span className="italic text-muted-foreground">{DELETED_MESSAGE_LABEL}</span>
        ) : (
          <MessageContent message={message} />
        )}
      </div>
    </li>
  );
}

function MessageContent({ message }: { message: MessageView }): ReactElement {
  return (
    <>
      {message.body ? <p>{message.body}</p> : null}
      {message.attachments.length > 0 ? (
        <ul
          aria-label="Attachments"
          className={cn("flex flex-col gap-1", message.body ? "mt-2" : "")}
        >
          {message.attachments.map((attachment) => (
            <li
              key={`${attachment.kind}:${attachment.fileName}`}
              className="truncate rounded-md border border-border bg-background px-2 py-1 text-body-sm text-muted-foreground"
            >
              {attachmentLabel(attachment)}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/** "Load older messages" at the top of a thread, with a retry line after a failure. */
function LoadOlder({
  status,
  onClick,
}: {
  status: OlderMessages["status"];
  onClick: () => void;
}): ReactElement {
  return (
    <div className="mb-4 flex flex-col items-start gap-1">
      <Button
        type="button"
        variant="outline"
        disabled={status === "loading"}
        onClick={onClick}
      >
        {LOAD_OLDER_LABEL}
      </Button>
      {status === "failed" ? (
        <p className="text-body-sm text-muted-foreground">
          {LOAD_OLDER_FAILED_MESSAGE}
        </p>
      ) : null}
    </div>
  );
}

/**
 * One RD's thread with the Client: an earlier RD's, labeled with their name and
 * read-only, or the viewer's own. Messages read oldest to newest.
 */
export function ThreadSection({
  thread,
  older,
  onLoadOlder,
  timezone,
  now,
}: {
  thread: MessageThreadView;
  older?: OlderMessages;
  onLoadOlder?: (threadId: string, cursor: string) => void;
  timezone: string;
  now: Date;
}): ReactElement {
  const view = olderView(thread, older);
  const heading = thread.own
    ? "Conversation"
    : `${thread.rdName?.trim() || "An earlier RD"} (earlier RD, read-only)`;

  return (
    <section
      aria-label={heading}
      data-thread-id={thread.threadId}
      data-thread-own={thread.own}
      className={cn(
        "mt-6 overflow-hidden rounded-xl border border-border bg-card",
        THEMED_CARD
      )}
    >
      <h2 className="border-b border-border px-5 py-3 font-sans text-body-lg font-semibold text-foreground">
        {heading}
      </h2>
      <div className="px-5 py-4">
        {view.cursor !== null && onLoadOlder ? (
          <LoadOlder
            status={view.status}
            onClick={() => onLoadOlder(thread.threadId, view.cursor ?? "")}
          />
        ) : null}
        {view.messages.length === 0 ? (
          <p className="text-body text-muted-foreground">
            {NO_THREAD_MESSAGES_MESSAGE}
          </p>
        ) : (
          <ol className="flex flex-col gap-4">
            {view.messages.map((message) => (
              <MessageItem
                key={message.id}
                message={message}
                timezone={timezone}
                now={now}
              />
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

/** A Client's name over their threads: earlier RDs' first, the viewer's own last. */
export function ClientThreads({
  threads,
  older = {},
  onLoadOlder,
  timezone,
  now,
}: {
  threads: MessageThreadView[];
  older?: Record<string, OlderMessages>;
  onLoadOlder?: (threadId: string, cursor: string) => void;
  timezone: string;
  now: Date;
}): ReactElement {
  if (threads.length === 0) {
    return <p className={NEUTRAL_MESSAGE}>{NO_THREAD_MESSAGES_MESSAGE}</p>;
  }

  return (
    <>
      {threads.map((thread) => (
        <ThreadSection
          key={thread.threadId}
          thread={thread}
          older={older[thread.threadId]}
          onLoadOlder={onLoadOlder}
          timezone={timezone}
          now={now}
        />
      ))}
    </>
  );
}
